import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, InputNumber, message, Space, Divider, Alert, Tabs } from 'antd';
import { SaveOutlined, ReloadOutlined, VideoCameraOutlined } from '@ant-design/icons';

const { TextArea } = Input;
const { TabPane } = Tabs;

interface BroadcastContent {
  zhTw: string; // 繁体中文
  en: string; // 英语
  vi: string; // 越南语
}

interface LiveRoomConfigData {
  // 中大奖配置
  jackpotAmountThreshold: number; // 中大奖金额阈值
  jackpotBroadcastContent: BroadcastContent; // 中大奖播报内容（多语言）
  jackpotTopDuration: number; // 中大奖播报置顶时长（秒）
  
  // 打赏配置
  tipAmountThreshold: number; // 打赏金额阈值
  tipBroadcastContent: BroadcastContent; // 打赏播报内容（多语言）
  tipTopDuration: number; // 打赏播报置顶时长（秒）
  
  // 直播间链接配置
  liveRoomLinkTemplate: string; // 直播间链接模板
}

const LiveRoomConfig: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // 初始化数据
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      // 模拟API调用
      await new Promise((resolve) => setTimeout(resolve, 100));
      const mockConfig: LiveRoomConfigData = {
        jackpotAmountThreshold: 1000,
        jackpotBroadcastContent: {
          zhTw: '恭喜 {username} 在 {gameName} 中大獎 {amount} 元！',
          en: 'Congratulations {username} won {amount} in {gameName}!',
          vi: 'Chúc mừng {username} trúng giải lớn {amount} trong {gameName}!',
        },
        jackpotTopDuration: 30,
        tipAmountThreshold: 100,
        tipBroadcastContent: {
          zhTw: '感謝 {username} 打賞 {amount} 元！',
          en: 'Thank you {username} for tipping {amount}!',
          vi: 'Cảm ơn {username} đã tip {amount}!',
        },
        tipTopDuration: 15,
        liveRoomLinkTemplate: '/live-room/{gameId}/{liveRoomId}',
      };
      form.setFieldsValue(mockConfig);
    } catch (error) {
      message.error('加载配置失败');
    }
  };

  const handleSave = async (values: LiveRoomConfigData) => {
    setLoading(true);
    try {
      // 模拟API调用
      await new Promise((resolve) => setTimeout(resolve, 1000));
      console.log('保存配置:', values);
      message.success('直播间配置保存成功！');
    } catch (error) {
      message.error('保存失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    loadConfig();
    message.info('已重置为默认设置');
  };

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <h2>
            <VideoCameraOutlined /> 直播间配置
          </h2>
          <p style={{ color: '#666', marginTop: 8 }}>
            配置直播间中大奖和打赏的播报规则，包括金额阈值、播报内容和置顶时长
          </p>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          initialValues={{
            jackpotAmountThreshold: 1000,
            jackpotBroadcastContent: {
              zhTw: '恭喜 {username} 在 {gameName} 中大獎 {amount} 元！',
              en: 'Congratulations {username} won {amount} in {gameName}!',
              vi: 'Chúc mừng {username} trúng giải lớn {amount} trong {gameName}!',
            },
            jackpotTopDuration: 30,
            tipAmountThreshold: 100,
            tipBroadcastContent: {
              zhTw: '感謝 {username} 打賞 {amount} 元！',
              en: 'Thank you {username} for tipping {amount}!',
              vi: 'Cảm ơn {username} đã tip {amount}!',
            },
            tipTopDuration: 15,
            liveRoomLinkTemplate: '/live-room/{gameId}/{liveRoomId}',
          }}
        >
          {/* 中大奖配置 */}
          <Card
            title="中大奖配置"
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Alert
              message="配置说明"
              description={
                <div>
                  <div style={{ marginBottom: 8 }}>当用户中奖金额达到或超过阈值时，将触发播报。播报内容支持变量：</div>
                  <div style={{ marginBottom: 4 }}>• {`{username}`} - 用户名</div>
                  <div style={{ marginBottom: 4 }}>• {`{gameName}`} - 游戏名称</div>
                  <div style={{ marginBottom: 4 }}>• {`{amount}`} - 中奖金额</div>
                  <div style={{ marginBottom: 4 }}>• {`{gameId}`} - 游戏ID</div>
                  <div style={{ marginBottom: 4 }}>• {`{liveRoomId}`} - 直播间ID（一个游戏可以开设多个直播间）</div>
                  <div style={{ marginTop: 8, color: '#1890ff', fontWeight: 500 }}>
                    💡 播报内容会自动变成可点击链接，点击后跳转到对应的直播间
                  </div>
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form.Item
              label="中大奖金额阈值（元）"
              name="jackpotAmountThreshold"
              tooltip="当用户中奖金额达到或超过此阈值时，将触发播报"
              rules={[
                { required: true, message: '请输入中大奖金额阈值' },
                { type: 'number', min: 1, message: '金额阈值必须大于0' },
              ]}
            >
              <InputNumber
                min={1}
                style={{ width: '100%' }}
                placeholder="请输入中大奖金额阈值"
              />
            </Form.Item>

            <Form.Item
              label="中大奖播报内容"
              tooltip="播报内容支持变量：{username}、{gameName}、{amount}、{gameId}、{liveRoomId}。播报内容会自动变成可点击链接，点击后跳转到对应直播间。"
              required
            >
              <Tabs defaultActiveKey="zhTw">
                <TabPane tab="繁体中文" key="zhTw">
                  <Form.Item
                    name={['jackpotBroadcastContent', 'zhTw']}
                    rules={[
                      { required: true, message: '请输入繁体中文播报内容' },
                      { max: 200, message: '播报内容不能超过200个字符' },
                    ]}
                    noStyle
                  >
                    <TextArea
                      rows={4}
                      placeholder="请输入繁体中文播报内容，支持变量：{username}、{gameName}、{amount}、{gameId}、{liveRoomId}"
                      showCount
                      maxLength={200}
                    />
                  </Form.Item>
                </TabPane>
                <TabPane tab="英语" key="en">
                  <Form.Item
                    name={['jackpotBroadcastContent', 'en']}
                    rules={[
                      { required: true, message: '请输入英语播报内容' },
                      { max: 200, message: '播报内容不能超过200个字符' },
                    ]}
                    noStyle
                  >
                    <TextArea
                      rows={4}
                      placeholder="Enter English broadcast content, supports variables: {username}, {gameName}, {amount}, {gameId}, {liveRoomId}"
                      showCount
                      maxLength={200}
                    />
                  </Form.Item>
                </TabPane>
                <TabPane tab="越南语" key="vi">
                  <Form.Item
                    name={['jackpotBroadcastContent', 'vi']}
                    rules={[
                      { required: true, message: '请输入越南语播报内容' },
                      { max: 200, message: '播报内容不能超过200个字符' },
                    ]}
                    noStyle
                  >
                    <TextArea
                      rows={4}
                      placeholder="Nhập nội dung phát sóng tiếng Việt, hỗ trợ biến: {username}, {gameName}, {amount}, {gameId}, {liveRoomId}"
                      showCount
                      maxLength={200}
                    />
                  </Form.Item>
                </TabPane>
              </Tabs>
            </Form.Item>

            <Form.Item
              label="播报置顶时长（秒）"
              name="jackpotTopDuration"
              tooltip="中大奖播报在直播间置顶显示的时长"
              rules={[
                { required: true, message: '请输入播报置顶时长' },
                { type: 'number', min: 1, max: 300, message: '范围：1-300秒' },
              ]}
            >
              <InputNumber
                min={1}
                max={300}
                style={{ width: '100%' }}
                placeholder="请输入播报置顶时长（秒）"
                addonAfter="秒"
              />
            </Form.Item>
          </Card>

          <Divider />

          {/* 打赏配置 */}
          <Card
            title="打赏配置"
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Alert
              message="配置说明"
              description={
                <div>
                  <div style={{ marginBottom: 8 }}>当用户打赏金额达到或超过阈值时，将触发播报。播报内容支持变量：</div>
                  <div style={{ marginBottom: 4 }}>• {`{username}`} - 用户名</div>
                  <div style={{ marginBottom: 4 }}>• {`{amount}`} - 打赏金额</div>
                  <div style={{ marginBottom: 4 }}>• {`{gameId}`} - 游戏ID</div>
                  <div style={{ marginBottom: 4 }}>• {`{liveRoomId}`} - 直播间ID（一个游戏可以开设多个直播间）</div>
                  <div style={{ marginTop: 8, color: '#1890ff', fontWeight: 500 }}>
                    💡 播报内容会自动变成可点击链接，点击后跳转到对应的直播间
                  </div>
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form.Item
              label="打赏金额阈值（元）"
              name="tipAmountThreshold"
              tooltip="当用户打赏金额达到或超过此阈值时，将触发播报"
              rules={[
                { required: true, message: '请输入打赏金额阈值' },
                { type: 'number', min: 1, message: '金额阈值必须大于0' },
              ]}
            >
              <InputNumber
                min={1}
                style={{ width: '100%' }}
                placeholder="请输入打赏金额阈值"
              />
            </Form.Item>

            <Form.Item
              label="打赏播报内容"
              tooltip="播报内容支持变量：{username}、{amount}、{gameId}、{liveRoomId}。播报内容会自动变成可点击链接，点击后跳转到对应直播间。"
              required
            >
              <Tabs defaultActiveKey="zhTw">
                <TabPane tab="繁体中文" key="zhTw">
                  <Form.Item
                    name={['tipBroadcastContent', 'zhTw']}
                    rules={[
                      { required: true, message: '请输入繁体中文播报内容' },
                      { max: 200, message: '播报内容不能超过200个字符' },
                    ]}
                    noStyle
                  >
                    <TextArea
                      rows={4}
                      placeholder="请输入繁体中文播报内容，支持变量：{username}、{amount}、{gameId}、{liveRoomId}"
                      showCount
                      maxLength={200}
                    />
                  </Form.Item>
                </TabPane>
                <TabPane tab="英语" key="en">
                  <Form.Item
                    name={['tipBroadcastContent', 'en']}
                    rules={[
                      { required: true, message: '请输入英语播报内容' },
                      { max: 200, message: '播报内容不能超过200个字符' },
                    ]}
                    noStyle
                  >
                    <TextArea
                      rows={4}
                      placeholder="Enter English broadcast content, supports variables: {username}, {amount}, {gameId}, {liveRoomId}"
                      showCount
                      maxLength={200}
                    />
                  </Form.Item>
                </TabPane>
                <TabPane tab="越南语" key="vi">
                  <Form.Item
                    name={['tipBroadcastContent', 'vi']}
                    rules={[
                      { required: true, message: '请输入越南语播报内容' },
                      { max: 200, message: '播报内容不能超过200个字符' },
                    ]}
                    noStyle
                  >
                    <TextArea
                      rows={4}
                      placeholder="Nhập nội dung phát sóng tiếng Việt, hỗ trợ biến: {username}, {amount}, {gameId}, {liveRoomId}"
                      showCount
                      maxLength={200}
                    />
                  </Form.Item>
                </TabPane>
              </Tabs>
            </Form.Item>

            <Form.Item
              label="播报置顶时长（秒）"
              name="tipTopDuration"
              tooltip="打赏播报在直播间置顶显示的时长"
              rules={[
                { required: true, message: '请输入播报置顶时长' },
                { type: 'number', min: 1, max: 300, message: '范围：1-300秒' },
              ]}
            >
              <InputNumber
                min={1}
                max={300}
                style={{ width: '100%' }}
                placeholder="请输入播报置顶时长（秒）"
                addonAfter="秒"
              />
            </Form.Item>
          </Card>

          <Divider />

          {/* 直播间链接配置 */}
          <Card
            title="直播间链接配置"
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Alert
              message="链接配置说明"
              description={
                <div>
                  <div style={{ marginBottom: 8 }}>配置直播间链接模板，用于生成可点击的播报链接。支持变量：</div>
                  <div style={{ marginBottom: 4 }}>• {`{gameId}`} - 游戏ID</div>
                  <div style={{ marginBottom: 4 }}>• {`{liveRoomId}`} - 直播间ID（一个游戏可以开设多个直播间）</div>
                  <div style={{ marginTop: 8, color: '#ff4d4f' }}>
                    ⚠️ 注意：播报内容会自动变成可点击链接，点击后使用此模板跳转到对应直播间
                  </div>
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form.Item
              label="直播间链接模板"
              name="liveRoomLinkTemplate"
              tooltip="直播间链接模板，支持变量：{gameId}、{liveRoomId}。例如：/live-room/{gameId}/{liveRoomId} 或 /room?gameId={gameId}&roomId={liveRoomId}"
              rules={[
                { required: true, message: '请输入直播间链接模板' },
                { max: 500, message: '链接模板不能超过500个字符' },
              ]}
            >
              <Input
                placeholder="例如：/live-room/{gameId}/{liveRoomId} 或 /room?gameId={gameId}&roomId={liveRoomId}"
                maxLength={500}
              />
            </Form.Item>
          </Card>

          {/* 操作按钮 */}
          <div style={{ textAlign: 'right', marginTop: 24 }}>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                重置
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                htmlType="submit"
                loading={loading}
              >
                保存配置
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default LiveRoomConfig;

